import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link2, CheckCircle, XCircle, AlertCircle, Clock, RefreshCw, ExternalLink, CheckCircle2, XOctagon } from 'lucide-react';

const ConnectorsMarketplace = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const platformInfo = {
    facebook: {
      name: 'Facebook',
      icon: '👥',
      color: '#1877F2',
      description: 'Publiez sur vos pages Facebook'
    },
    instagram: {
      name: 'Instagram',
      icon: '📸',
      color: '#E4405F',
      description: 'Partagez photos et vidéos sur Instagram'
    },
    linkedin: {
      name: 'LinkedIn',
      icon: '💼',
      color: '#0A66C2',
      description: 'Publiez du contenu professionnel'
    },
    x: {
      name: 'X (Twitter)',
      icon: '✖️',
      color: '#000000',
      description: 'Tweetez et engagez votre audience'
    },
    tiktok: {
      name: 'TikTok',
      icon: '🎵',
      color: '#000000',
      description: 'Créez des vidéos virales'
    },
    youtube: {
      name: 'YouTube',
      icon: '▶️',
      color: '#FF0000',
      description: 'Publiez des vidéos longues'
    }
  };

  useEffect(() => {
    fetchConnectors();
    
    // Check for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const platform = urlParams.get('platform');
    
    if (success && platform) {
      setNotification({
        type: 'success',
        message: `${platformInfo[platform]?.name || platform} connecté avec succès !`
      });
      
      // Clean URL
      window.history.replaceState({}, '', '/connectors');
      
      // Refresh connectors
      setTimeout(() => fetchConnectors(), 500);
    } else if (error && platform) {
      const errorMessages = {
        'invalid_state': 'Session expirée. Veuillez réessayer.',
        'token_exchange_failed': 'Échec de l\'échange de token. Vérifiez vos credentials OAuth.',
        'access_denied': 'Accès refusé. Vous devez autoriser l\'application.'
      };
      
      setNotification({
        type: 'error',
        message: `Erreur ${platformInfo[platform]?.name || platform}: ${errorMessages[error] || error}`
      });
      
      // Clean URL
      window.history.replaceState({}, '', '/connectors');
    }
  }, []);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchConnectors = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connectors`, {
        credentials: 'include'
      });

      const result = await response.json();
      if (result.success) {
        setConnectors(result.connectors || []);
      }
    } catch (error) {
      console.error('Error fetching connectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connectors/${platform}/connect`, {
        method: 'POST',
        credentials: 'include'
      });

      const result = await response.json();
      if (result.success && result.oauth_url) {
        // Redirect to OAuth URL (full page redirect)
        window.location.href = result.oauth_url;
      } else {
        setNotification({
          type: 'error',
          message: `OAuth non configuré pour ${platformInfo[platform].name}. Ajoutez les credentials dans le backend.`
        });
      }
    } catch (error) {
      console.error('Error connecting:', error);
      setNotification({
        type: 'error',
        message: `Erreur lors de la connexion à ${platformInfo[platform].name}`
      });
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Déconnecter ${platformInfo[platform].name} ?`)) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connectors/${platform}/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });

      setNotification({
        type: 'success',
        message: `${platformInfo[platform].name} déconnecté`
      });

      fetchConnectors();
    } catch (error) {
      console.error('Error disconnecting:', error);
      setNotification({
        type: 'error',
        message: `Erreur lors de la déconnexion`
      });
    }
  };

  const handleRefresh = async (platform) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connectors/${platform}/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      const result = await response.json();
      if (result.success) {
        setNotification({
          type: 'success',
          message: `Token ${platformInfo[platform].name} actualisé`
        });
        fetchConnectors();
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setNotification({
        type: 'error',
        message: `Échec de l'actualisation du token`
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'error':
        return <AlertCircle className="text-red-400" size={20} />;
      case 'expired':
        return <Clock className="text-orange-400" size={20} />;
      case 'not_configured':
        return <AlertCircle className="text-yellow-400" size={20} />;
      default:
        return <XCircle className="text-slate-500" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connecté';
      case 'error':
        return 'Erreur';
      case 'expired':
        return 'Expiré';
      case 'not_configured':
        return 'Non configuré';
      default:
        return 'Déconnecté';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'border-green-500/30 bg-green-500/10';
      case 'error':
        return 'border-red-500/30 bg-red-500/10';
      case 'expired':
        return 'border-orange-500/30 bg-orange-500/10';
      case 'not_configured':
        return 'border-yellow-500/30 bg-yellow-500/10';
      default:
        return 'border-slate-500/30 bg-slate-500/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl backdrop-blur-xl border ${
            notification.type === 'success'
              ? 'bg-green-500/20 border-green-500/50 text-green-300'
              : 'bg-red-500/20 border-red-500/50 text-red-300'
          } shadow-lg`}
          data-testid="notification"
        >
          <div className="flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle2 size={20} />
            ) : (
              <XOctagon size={20} />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-xl bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Link2 className="inline mr-3 text-cyan-400" size={36} />
                Connectors Marketplace
              </h1>
              <p className="text-slate-400 text-lg">Connectez vos réseaux sociaux pour publier automatiquement</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={fetchConnectors}
              className="px-6 py-3 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/20 transition-all duration-200"
              data-testid="refresh-button"
            >
              <RefreshCw size={20} className="inline mr-2" />
              Actualiser
            </motion.button>
          </div>
        </div>
      </div>

      {/* Connectors Grid */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(platformInfo).map(([platform, info], index) => {
            const connector = connectors.find(c => c.platform === platform) || { platform, status: 'disconnected' };
            const isConnected = connector.status === 'connected';

            return (
              <motion.div
                key={platform}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`backdrop-blur-xl rounded-2xl border p-6 ${getStatusColor(connector.status)}`}
                data-testid={`connector-${platform}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{info.icon}</div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{info.name}</h3>
                      <p className="text-slate-400 text-sm">{info.description}</p>
                    </div>
                  </div>
                  {getStatusIcon(connector.status)}
                </div>

                {/* Status */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-400">État:</span>
                    <span className="text-white font-medium">{getStatusText(connector.status)}</span>
                  </div>
                  {connector.account_username && (
                    <div className="flex items-center gap-2 text-sm mt-1">
                      <span className="text-slate-400">Compte:</span>
                      <span className="text-cyan-400">@{connector.account_username}</span>
                    </div>
                  )}
                  {connector.connected_at && (
                    <div className="text-xs text-slate-500 mt-1">
                      Connecté le {new Date(connector.connected_at).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                  {connector.last_error && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                      {connector.last_error}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {connector.status === 'not_configured' ? (
                    <div className="w-full px-4 py-3 bg-yellow-500/10 text-yellow-400 rounded-xl border border-yellow-500/30 text-center text-sm">
                      Configuration OAuth requise
                    </div>
                  ) : !isConnected ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleConnect(platform)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200"
                      data-testid={`connect-${platform}`}
                    >
                      <ExternalLink size={18} className="inline mr-2" />
                      Connecter
                    </motion.button>
                  ) : (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleRefresh(platform)}
                        className="w-full px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-sm"
                        data-testid={`refresh-${platform}`}
                      >
                        <RefreshCw size={16} className="inline mr-2" />
                        Actualiser token
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleConnect(platform)}
                        className="w-full px-4 py-2 bg-slate-500/10 text-slate-400 rounded-xl border border-slate-500/30 hover:bg-slate-500/20 transition-all text-sm"
                        data-testid={`reconnect-${platform}`}
                      >
                        <RefreshCw size={16} className="inline mr-2" />
                        Reconnecter
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleDisconnect(platform)}
                        className="w-full px-4 py-2 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30 hover:bg-red-500/20 transition-all text-sm"
                        data-testid={`disconnect-${platform}`}
                      >
                        <XCircle size={16} className="inline mr-2" />
                        Déconnecter
                      </motion.button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-cyan-500/10 backdrop-blur-xl rounded-2xl border border-cyan-500/30 p-6">
          <h3 className="text-white font-semibold text-lg mb-2 flex items-center gap-2">
            <AlertCircle className="text-cyan-400" size={20} />
            Configuration OAuth
          </h3>
          <p className="text-slate-300 text-sm mb-3">
            Pour connecter vos comptes sociaux, vous devez configurer les clés API OAuth dans les variables d'environnement backend (.env).
          </p>
          <ul className="text-slate-400 text-sm space-y-1">
            <li>• <strong>Facebook & Instagram:</strong> FACEBOOK_APP_ID, FACEBOOK_APP_SECRET</li>
            <li>• <strong>LinkedIn:</strong> LINKEDIN_CLIENT_ID, LINKEDIN_CLIENT_SECRET</li>
            <li>• <strong>X (Twitter):</strong> X_CLIENT_ID, X_CLIENT_SECRET</li>
            <li>• <strong>TikTok:</strong> TIKTOK_CLIENT_KEY, TIKTOK_CLIENT_SECRET</li>
            <li>• <strong>YouTube:</strong> YOUTUBE_CLIENT_ID, YOUTUBE_CLIENT_SECRET</li>
          </ul>
          <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <p className="text-xs text-slate-400">
              📚 Consultez <code className="text-cyan-400">/app/docs/oauth/</code> pour les guides de configuration détaillés par plateforme.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectorsMarketplace;
